import { useCallback } from 'react';

import { LabelId, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { modifyEmailLabelsUnified } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';

const CATEGORY_LABELS = [
  LabelId.CATEGORY_PERSONAL,
  LabelId.CATEGORY_SOCIAL,
  LabelId.CATEGORY_PROMOTIONS,
  LabelId.CATEGORY_FORUMS,
  LabelId.CATEGORY_UPDATES,
] as const;

const CATEGORY_MAP: Record<string, LabelId> = {
  inbox: LabelId.INBOX,
  social: LabelId.CATEGORY_SOCIAL,
  promotions: LabelId.CATEGORY_PROMOTIONS,
  forums: LabelId.CATEGORY_FORUMS,
  updates: LabelId.CATEGORY_UPDATES,
};

export const useMoveToCategoryUserCommunication = () => {
  const {
    setMoveToInboxLoading,
    updateUserCommunicationLabels,
    getUserCommunicationByMessageId,
  } = useUserCommunicationsStore();

  const handleMoveToCategoryUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      targetCategory: string,
      isFromSpam?: boolean,
      onSuccess?: () => void,
    ) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const targetLabelId = CATEGORY_MAP[targetCategory];
      if (!targetLabelId) {
        log.error(`Invalid target category: ${targetCategory}`);
        return;
      }

      const communicationsToMove = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (communicationsToMove.length === 0) {
        log.error('No valid communications to move to category');
        return;
      }

      onSuccess?.();

      setMoveToInboxLoading(true);

      const labelBackupMap = new Map<string, LabelId[]>();

      communicationsToMove.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);

        const currentLabels = communication.labelIds || [];

        const currentCategoryLabel = currentLabels.find((label) =>
          CATEGORY_LABELS.includes(label as (typeof CATEGORY_LABELS)[number]),
        );

        let newLabelIds = [...currentLabels];

        if (currentCategoryLabel) {
          newLabelIds = newLabelIds.filter(
            (label) => label !== currentCategoryLabel,
          );
        }

        if (targetCategory !== 'inbox') {
          newLabelIds.push(targetLabelId);
        }

        if (!newLabelIds.includes(LabelId.INBOX)) {
          newLabelIds.push(LabelId.INBOX);
        }

        if (isFromSpam && newLabelIds.includes(LabelId.SPAM)) {
          newLabelIds = newLabelIds.filter((label) => label !== LabelId.SPAM);
        }

        updateUserCommunicationLabels(messageId, newLabelIds);
      });

      try {
        const operations = communicationsToMove.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              const currentLabels = communication.labelIds || [];
              const currentCategoryLabel = currentLabels.find((label) =>
                CATEGORY_LABELS.includes(
                  label as (typeof CATEGORY_LABELS)[number],
                ),
              );

              const addLabelIds: string[] = [];
              const removeLabelIds: string[] = [];

              if (currentCategoryLabel) {
                removeLabelIds.push(currentCategoryLabel);
              }

              if (targetCategory !== 'inbox') {
                addLabelIds.push(targetLabelId);
              }

              if (isFromSpam) {
                addLabelIds.push(LabelId.INBOX);
                removeLabelIds.push(LabelId.SPAM);
              }

              await modifyEmailLabelsUnified({
                emailAddress: communication.userEmailAddress,
                platform: communication.platform!,
                messageIds: [communication.messageId],
                addLabelIds,
                removeLabelIds,
              });

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to move email to category ${targetCategory} for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            communicationsToMove.length === 1
              ? `Message moved to ${targetCategory} successfully`
              : `${communicationsToMove.length} messages moved to ${targetCategory} successfully`,
          );
        } else {
          failed.forEach(({ messageId }) => {
            const previousLabels = labelBackupMap.get(messageId);
            if (previousLabels) {
              updateUserCommunicationLabels(messageId, previousLabels);
            }
          });

          log.warn(
            `${failed.length} operations failed out of ${communicationsToMove.length}`,
          );
          log.error(
            failed.length === communicationsToMove.length
              ? `Failed to move message(s) to ${targetCategory}`
              : `${failed.length} of ${communicationsToMove.length} messages failed to be moved to ${targetCategory}`,
          );
        }
      } catch (error) {
        log.error(`Failed to move message(s) to ${targetCategory}:`, error);
      } finally {
        setMoveToInboxLoading(false);
      }
    },
    [
      setMoveToInboxLoading,
      updateUserCommunicationLabels,
      getUserCommunicationByMessageId,
    ],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleMoveToCategoryUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
